/**
 *
 * GuideStatus
 *
 */

import React, { memo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import type { PopconfirmProps } from 'antd';
import { Button, Col, Popconfirm, Row, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { Loading } from '../../components/Loading';

import { useQuery } from '@apollo/client';
import { GET_GUIDE_STATUS_LIST } from '../../graphql/queries';

import EditGuideStatusModal from './EditGuideStatusModal';
import NewGuideStatusModal from './NewGuideStatusModal';

import { DeleteGuideStatus } from 'app/redux/actions/initiatives';

const { Title } = Typography;

export const GuideStatus = memo(() => {
  const dispatch = useDispatch();

  const [newGuideStatusModal, setNewGuideStatusModal] = useState(false);
  const [editGuideStatusModal, setEditGuideStatusModal] = useState(false);

  const [newGuideStatusModalKey, setNewGuideStatusModalKey] = useState(0);
  const [editGuideStatusModalKey, setEditGuideStatusModalKey] = useState(0);

  const [data, setData] = useState(null);

  const {
    loading: guideStatusListLoading,
    data: guideStatusList,
    refetch: guideStatusRefetch,
  } = useQuery(GET_GUIDE_STATUS_LIST);

  const confirm = async (recordID: number) => {
    await dispatch(DeleteGuideStatus(recordID));
    guideStatusRefetch();
  };

  const cancel: PopconfirmProps['onCancel'] = () => {};

  const columns: ColumnsType<any> = [
    {
      title: 'Title',
      align: 'center' as const,
      render: (data: any) => {
        return data.node.title;
      },
    },
    {
      title: 'Description',
      align: 'center' as const,
      render: (data: any) => {
        return data.node.description;
      },
    },
    {
      title: 'Actions',
      align: 'center',
      render: (_: any, record: any) => {
        return (
          <Space size="middle">
            <Button
              type="text"
              onClick={() => {
                setData(record.node);
                setEditGuideStatusModal(true);
                setEditGuideStatusModalKey(Date.now());
              }}
            >
              <EditOutlined style={{ fontSize: '150%' }} />
            </Button>

            <Popconfirm
              title="Delete Record"
              description="Are you sure to delete this record?"
              onConfirm={() => confirm(record.node.pk)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            >
              <Button type="text">
                <DeleteOutlined style={{ fontSize: '150%' }} />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  if (guideStatusListLoading) return <Loading />;

  return (
    <>
      <Row>
        <Col>
          <Title level={3}>
            Guide Status List
            <PlusCircleOutlined
              className="mx-3"
              onClick={() => {
                setNewGuideStatusModal(true);
                setNewGuideStatusModalKey(Date.now());
              }}
            />
          </Title>
        </Col>
      </Row>

      <Table
        dataSource={guideStatusList?.guideStatus?.edges}
        columns={columns}
        pagination={false}
        rowKey={record => record.node.pk}
      />

      <NewGuideStatusModal
        show={newGuideStatusModal}
        setShow={setNewGuideStatusModal}
        key={newGuideStatusModalKey}
      />

      {data && (
        <EditGuideStatusModal
          show={editGuideStatusModal}
          setShow={setEditGuideStatusModal}
          data={data}
          setData={setData}
          key={editGuideStatusModalKey}
        />
      )}
    </>
  );
});
